/**
 * Task Plan-Gate Count Algebra (Story 28.4 — mirrors alertPlanGate.ts / 27.2).
 *
 * PURE LEAF MODULE — no React, no fetch. The Tasks page's "Create a Task" button
 * and the create dialog's submit guard share THIS single count so the two
 * surfaces never disagree and the web count mirrors the server's getTaskCount
 * (apps/server/src/services/plan-limits.ts) exactly.
 *
 * 🔴 TENANT-WIDE on ALL tiers (free=5, starter=50, pro/agency=unlimited) — NO
 * per-client branch, UNLIKE countAlertsForGate. Takes NO `plan`/`clientId` args:
 * the count is the same regardless of plan; the caller looks up taskLimit
 * separately for the limit comparison.
 *
 * 🔴 DEMO EXCLUSION: a task whose client is in demo mode is NEVER counted (server
 * parity with `c.demo_mode = 0`). A client-less task (clientId === null) is never
 * a demo task → it counts (parity with the server `OR c.id IS NULL`).
 *
 * 🔴 LOADING: if `tasks` or `clients` is undefined (queries in flight), returns
 * `undefined`; the page passes that to useFeatureGate whose `checked: false`
 * disables the Create button — creation is never allowed past the gate on an
 * unknown count.
 *
 * 🔴 CONTRACT: `clients` MUST contain EVERY client of the tenant, including demo
 * clients (the page's ["clients"] query returns all of them, demo included). The
 * demo set is derived from this array, so a demo client ABSENT from `clients`
 * would be counted here while the server's getTaskCount (which JOINs the real
 * clients table) excludes it. Passing a filtered/paginated client list breaks
 * web↔server parity. This is only a client-side estimate — the server is the
 * authoritative gate (requireFeature on POST /api/tasks, wired in 28.2); worst
 * case a slightly-high count disables Create early, never a bypass. An orphan
 * client_id with NO matching client row IS counted (matching the server's
 * LEFT JOIN → c.id IS NULL → counted).
 */

/** Minimal task shape the gate needs (clientId nullable — general tasks). */
export interface TaskGateInput {
  clientId: string | null;
}

/** Minimal client shape the gate needs. */
export interface ClientGateInput {
  id: string;
  demoMode: boolean;
}

/**
 * Returns the tenant-wide, demo-excluded task count to compare against the
 * plan's taskLimit, or `undefined` while inputs load.
 */
export function countTasksForGate(
  tasks: readonly TaskGateInput[] | undefined,
  clients: readonly ClientGateInput[] | undefined,
): number | undefined {
  if (tasks === undefined || clients === undefined) return undefined;

  const demoClientIds = new Set(
    clients.filter((c) => c.demoMode).map((c) => c.id),
  );

  // Tenant-wide: a general task (clientId null) is never demo → counts; a
  // real-client task counts unless its client is in demo mode.
  const nonDemo = tasks.filter(
    (t) => t.clientId === null || !demoClientIds.has(t.clientId),
  );

  return nonDemo.length;
}
